/**
 * Exemplo de uso do Wizard
 *
 * Este arquivo demonstra como usar o sistema de wizard para criar
 * um fluxo multi-step com navegação e gerenciamento de estado.
 */

import { useState } from 'react';
import { Button, TextField, Typography, Box } from '@mui/material';
import { Wizard } from './components/wizard';
import { WizardProvider } from './context';
import type { WizardSteps, WizardValuesBase } from './types';
import { useWizard } from './index';

// 1. Defina a interface dos valores que serão armazenados no wizard
interface FormValues extends WizardValuesBase {
  // Step 1: Informações pessoais
  name: string;
  email: string;

  // Step 2: Endereço
  street: string;
  city: string;
  zipCode: string;

  // Step 3: Confirmação (não precisa de campos adicionais)
}

// 2. Crie os componentes de cada step
// Cada step pode usar o hook useWizard para acessar e modificar o estado

const PersonalInfoStep = () => {
  const { values, actions } = useWizard<FormValues>();
  const [localName, setLocalName] = useState(values.name || '');
  const [localEmail, setLocalEmail] = useState(values.email || '');

  const handleNext = () => {
    // Salva os valores antes de navegar
    actions.updateValues({
      name: localName,
      email: localEmail,
    });
    actions.navigate('address');
  };

  return (
    <Box>
      <TextField
        fullWidth
        label="Nome completo"
        value={localName}
        onChange={(e) => setLocalName(e.target.value)}
        margin="normal"
      />
      <TextField
        fullWidth
        label="Email"
        type="email"
        value={localEmail}
        onChange={(e) => setLocalEmail(e.target.value)}
        margin="normal"
      />
      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          onClick={handleNext}
          disabled={!localName || !localEmail}
        >
          Próximo
        </Button>
      </Box>
    </Box>
  );
};

const AddressStep = () => {
  const { values, actions } = useWizard<FormValues>();
  const [localStreet, setLocalStreet] = useState(values.street || '');
  const [localCity, setLocalCity] = useState(values.city || '');
  const [localZipCode, setLocalZipCode] = useState(values.zipCode || '');

  const handleNext = () => {
    actions.updateValues({
      street: localStreet,
      city: localCity,
      zipCode: localZipCode,
    });
    actions.navigate('confirmation');
  };

  const handleBack = () => {
    actions.navigate('personal');
  };

  return (
    <Box>
      <TextField
        fullWidth
        label="Rua"
        value={localStreet}
        onChange={(e) => setLocalStreet(e.target.value)}
        margin="normal"
      />
      <TextField
        fullWidth
        label="Cidade"
        value={localCity}
        onChange={(e) => setLocalCity(e.target.value)}
        margin="normal"
      />
      <TextField
        fullWidth
        label="CEP"
        value={localZipCode}
        onChange={(e) => setLocalZipCode(e.target.value)}
        margin="normal"
      />
      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between' }}>
        <Button variant="outlined" onClick={handleBack}>
          Voltar
        </Button>
        <Button
          variant="contained"
          onClick={handleNext}
          disabled={!localStreet || !localCity || !localZipCode}
        >
          Próximo
        </Button>
      </Box>
    </Box>
  );
};

const ConfirmationStep = () => {
  const { values, actions } = useWizard<FormValues>();

  const handleBack = () => {
    actions.navigate('address');
  };

  const handleSubmit = () => {
    // Aqui você faria a submissão final dos dados
    console.log('Dados finais:', values);
    alert('Cadastro concluído com sucesso!');
  };

  return (
    <Box>
      <Typography variant="h2" gutterBottom>
        Confirme seus dados
      </Typography>

      <Box sx={{ my: 2 }}>
        <Typography variant="h3" color="text.secondary">
          Informações Pessoais
        </Typography>
        <Typography>Nome: {values.name}</Typography>
        <Typography>Email: {values.email}</Typography>
      </Box>

      <Box sx={{ my: 2 }}>
        <Typography variant="h3" color="text.secondary">
          Endereço
        </Typography>
        <Typography>Rua: {values.street}</Typography>
        <Typography>Cidade: {values.city}</Typography>
        <Typography>CEP: {values.zipCode}</Typography>
      </Box>

      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between' }}>
        <Button variant="outlined" onClick={handleBack}>
          Voltar
        </Button>
        <Button variant="contained" color="success" onClick={handleSubmit}>
          Confirmar
        </Button>
      </Box>
    </Box>
  );
};

// 3. Configure os steps do wizard
const steps: WizardSteps = {
  personal: {
    component: PersonalInfoStep,
    titleElement: (
      <Typography variant="h2" gutterBottom>
        Passo 1: Informações Pessoais
      </Typography>
    ),
  },
  address: {
    component: AddressStep,
    titleElement: (
      <Typography variant="h2" gutterBottom>
        Passo 2: Endereço
      </Typography>
    ),
  },
  confirmation: {
    component: ConfirmationStep,
    titleElement: (
      <Typography variant="h2" gutterBottom>
        Passo 3: Confirmação
      </Typography>
    ),
  },
};

// 4. Crie o componente principal que usa o WizardProvider
export const WizardExample = () => {
  // Estado inicial do wizard
  const initialState = {
    currentStepId: 'personal', // Step inicial
    values: {
      // Valores iniciais (podem ser vazios)
      name: '',
      email: '',
      street: '',
      city: '',
      zipCode: '',
    } as FormValues,
  };

  return (
    <Box sx={{ maxWidth: 600 }}>
      <WizardProvider<FormValues> initialState={initialState}>
        <Wizard steps={steps} />
      </WizardProvider>
    </Box>
  );
};

/**
 * EXEMPLO DE USO COM VALIDAÇÃO
 *
 * Você também pode adicionar validação mais complexa:
 */

export const WizardWithValidation = () => {
  const initialState = {
    currentStepId: 'personal',
    values: {} as FormValues,
  };

  return (
    <Box sx={{ maxWidth: 600 }}>
      <WizardProvider<FormValues> initialState={initialState}>
        <Wizard steps={steps} />
      </WizardProvider>
    </Box>
  );
};

/**
 * EXEMPLO COM ACTIONS CUSTOMIZADAS
 *
 * Se você precisar interceptar navegação ou salvar em localStorage:
 */

export const WizardWithCustomActions = () => {
  const customActions = {
    navigate: (stepId: string) => {
      console.log('Navegando para:', stepId);
      // Você pode adicionar lógica customizada aqui
    },
    updateValues: (values: Partial<FormValues>) => {
      console.log('Atualizando valores:', values);
      // Salvar no localStorage, por exemplo
      localStorage.setItem('wizardValues', JSON.stringify(values));
    },
  };

  const initialState = {
    currentStepId: 'personal',
    values: {} as FormValues,
    actions: customActions,
  };

  return (
    <Box sx={{ maxWidth: 600 }}>
      <WizardProvider<FormValues> initialState={initialState}>
        <Wizard steps={steps} />
      </WizardProvider>
    </Box>
  );
};

/**
 * RESUMO DE USO:
 *
 * 1. Defina a interface dos valores (FormValues extends WizardValuesBase)
 * 2. Crie os componentes de cada step usando useWizard<FormValues>()
 * 3. Configure o objeto steps com { [stepId]: { component, titleElement } }
 * 4. Envolva o Wizard com WizardProvider<FormValues>
 * 5. Use actions.navigate() para mudar de step
 * 6. Use actions.updateValues() para salvar dados
 * 7. Acesse values para ler os dados salvos
 */
